import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import {
  SelectLocationOnMap,
  GeneralButton,
  GeneralInput,
  GeneralSelectApi,
  Loader,
} from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { useGetData, usePostData } from "../../../hooks";
import { EditClinicAddressRequest } from "../../../interface/GeneralResponse";
import { EditLocationOptionType } from "../../../interface/OptionTypes";
import { RootState } from "../../../redux/store";
import { useToast } from "../../../Services/ToastContext";
import { EditProfileLayout } from "../component";

interface EditClinicAddressProp {
  onClose?: () => void;
  className?: string;
}

const EditClinicAddress: React.FC<EditClinicAddressProp> = ({
  onClose,
  className,
}) => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const locationInfoSchema = z.object({
    CityId: z
      .number()
      .nullable()
      .refine(
        (value) => {
          if (value === null || value <= 0) {
            return false;
          }
          return true;
        },
        {
          message: t("required_field"),
        },
      ),
    AreaId: z
      .number()
      .nullable()
      .refine((value) => value !== null, {
        message: t("required_field"),
      }),
    Address: z.string().min(1, t("required")),
    Latitude: z.number().nullable().optional(),
    Longitude: z.number().nullable().optional(),
  });
  const CountryId = useSelector(
    (state: RootState) => state.registration.userDetails.CountryId,
  );
  const {
    data: locationData,
    isPending: isFetchingLocation,
    isError: isLocationError,
    error: locationError,
    refetch,
  } = useGetData<EditClinicAddressRequest>(EndPoints.GetAddress);
  const queryClient = useQueryClient();
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const { mutate: updateLocationInfo, isPending: isUpdating } = usePostData(
    EndPoints.EditAddress,
  );

  const methods = useForm<EditClinicAddressRequest>({
    defaultValues: {
      CityId: null,
      AreaId: null,
      Address: "",
      Latitude: 0,
      Longitude: 0,
    },
    resolver: zodResolver(locationInfoSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    navigate(location.state?.from || -1);
  };

  const modalLayout = !location.pathname.includes("clinic_address");

  const {
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitted },
    watch,
  } = methods;

  useEffect(() => {
    if (locationData?.Data) {
      reset({
        ...locationData.Data,
      });
      setSelectedCityId(locationData.Data.CityId ?? null);
    }
  }, [locationData, reset]);
  useEffect(() => {
    if (isLocationError && locationError) {
      showToast(
        locationError.message || t("failed_to_fetch_address_info"),
        "error",
      );
    }
  }, [isLocationError, locationError, showToast, t]);

  const onSubmit: SubmitHandler<EditClinicAddressRequest> = (formData) => {
    updateLocationInfo(formData, {
      onSuccess: () => {
        showToast(t("location_info_edited_successfully"), "success");
        refetch();

        queryClient.refetchQueries({
          queryKey: [EndPoints.GetPersonalInfo],
          exact: false,
        });
        if (onClose) {
          onClose();
        } else {
          navigate("/profile/clinic_address");
        }
      },
      onError: (error: any) => {
        showToast(error.message || t("something_went_wrong"), "error");
      },
    });
  };

  const handleCityChange = (selectedOption: EditLocationOptionType | null) => {
    if (selectedOption) {
      const cityId = Number(selectedOption.value);
      setValue("CityId", cityId, { shouldValidate: true });
      setValue("AreaId", null, { shouldValidate: true });
      setSelectedCityId(cityId);
    } else {
      setValue("CityId", null, { shouldValidate: true });
      setValue("AreaId", null, { shouldValidate: true });
      setSelectedCityId(null);
    }
  };

  const handleAreaChange = (selectedOption: EditLocationOptionType | null) => {
    if (selectedOption) {
      const areaId = Number(selectedOption.value);
      setValue("AreaId", areaId, { shouldValidate: true });
    } else {
      setValue("AreaId", null, { shouldValidate: true });
    }
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setValue("Latitude", lat);
    setValue("Longitude", lng);
  };

  if (isLocationError) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <p className="text-error">{t("failed_to_load_address_info")}</p>
        <button
          onClick={() => refetch()}
          className="mt-4 rounded bg-primary px-4 py-2 text-white"
        >
          {t("retry")}
        </button>
      </div>
    );
  }

  const formContent = (
    <>
      <div>
        <GeneralSelectApi
          name="CityId"
          placeholder={t("city")}
          endpoint={EndPoints.Cities}
          allParams={{ CountryId: CountryId }}
          isClearable={false}
          onChange={handleCityChange}
        />
        {isSubmitted && errors.CityId && (
          <p className="my-1 text-sm text-error">{errors.CityId.message}</p>
        )}
      </div>
      <div>
        <GeneralSelectApi
          name="AreaId"
          placeholder={t("area")}
          isDisabled={!selectedCityId}
          allParams={{ cityId: Number(selectedCityId) ?? null }}
          enabled={Number(selectedCityId) ? true : false}
          endpoint={EndPoints.Area}
          onChange={handleAreaChange}
          isClearable={false}
          defaultValue={
            locationData?.Data.Area &&
            locationData.Data.CityId === selectedCityId
              ? {
                  value: locationData.Data.Area?.Id,
                  label: locationData.Data.Area?.Name,
                  key: `${locationData.Data.Area?.Id}`,
                }
              : undefined
          }
        />
        {isSubmitted && errors.AreaId && (
          <p className="my-1 text-sm text-error">{errors.AreaId.message}</p>
        )}
      </div>
      <GeneralInput
        type="textarea"
        name="Address"
        label={t("detailed_address")}
        placeholder={t("detailed_address")}
        maxLength={250}
        customStyleClasses="!max-h-24"
      />
    </>
  );

  return (
    <FormProvider {...methods}>
      <EditProfileLayout
        title={t("clinic_address")} 
        onDiscard={onClose ?? handleBack}
        onSave={() => handleSubmit(onSubmit)()}
        isSaving={isUpdating} 
      >
        <form
          className="flex h-full min-h-0 w-full flex-col"
          onSubmit={handleSubmit(onSubmit)}
        >
          {isFetchingLocation ? (
            <div className="flex h-full min-h-72 items-center justify-center">
              <Loader />
            </div>
          ) : (
            <div
              className={`flex min-h-0 flex-1 gap-4 ${
                modalLayout ? "flex-row" : "flex-col"
              }`}
            >
              <div
                className={`h-80 w-full shrink-0 max-lg:h-80 ${
                  modalLayout ? "w-1/2" : ""
                }`}
              >
                <SelectLocationOnMap
                  withEditBtn
                  onLocationSelect={handleLocationSelect}
                  defaultLocation={
                    watch("Latitude") && watch("Longitude")
                      ? {
                          lat: watch("Latitude") ?? 33.5138,
                          lng: watch("Longitude") ?? 36.2765,
                        }
                      : locationData?.Data
                        ? {
                            lat: locationData.Data.Latitude ?? 33.5138,
                            lng: locationData.Data.Longitude ?? 36.2765,
                          }
                        : undefined
                  }
                />
              </div>

              {modalLayout ? (
                <div className="min-h-0 w-1/2 overflow-y-auto p-2">
                  <div className="flex flex-col gap-4">{formContent}</div>
                </div>
              ) : (
                <div className="flex flex-col gap-4">{formContent}</div>
              )}
 
              <button type="submit" className="hidden" aria-hidden="true" />
            </div>
          )}
        </form>
      </EditProfileLayout>
    </FormProvider>
  );
};

export default EditClinicAddress;
